import { Fragment, useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';

import { alarmBlockerNote, alarmsSummary, orderAlarmsForDisplay } from '../../lib/revealStaging';
import type { RevealAlarm, RevealHealthIssue } from '../../lib/revealStaging';

// Stage 4 — "What we'll watch" (the Alarms stage). Renders the suggested alarms
// from the mirrored snapshot. Protect-the-magic behaviors baked in:
//   - task 7.3 (wrong-guess humility): orderAlarmsForDisplay keeps the
//     deterministic safety floor ABOVE the AI-suggested additions, and every
//     aiSuggested item is shown as a suggestion — labelled, de-emphasised,
//     never pre-checked as fact.
//   - task 7.2 (quality floor): alarmsSummary copy never reads as empty.
//   - reveal-alarm-review A2/A3: every row carries an explicit accept/reject
//     toggle, driven entirely by the `decisions` prop (derived from the
//     persisted snapshot upstream, never local component state).
//   - FTUE↔cloud parity B2: when a network-health issue blocked an alarm
//     recommendation boat-side, a quiet note names it (alarmBlockerNote).
//
// Presentation (ftue-reveal-flow-cleanup design pass): each alarm is a COMPACT
// one-line row (severity · title · Suggested/confidence · Why? · Watch|Not now);
// the long auto-generated explanation is collapsed by default behind a per-row
// "Why?" disclosure (presentation-only local state, never touches `decisions`).
// The working state ("Setting up…" / "Still checking…") is lifted OUT of the
// bottom of the list into an inline indicator beside the count AND a sticky
// footer that also carries the gated CTA — so the status and the forward action
// are always visible without scrolling.

const SEVERITY_LABELS: Record<string, string> = {
  emergency: 'Emergency',
  alarm: 'Alarm',
  warning: 'Warning',
  caution: 'Caution',
};

// building  = apply in flight, no alarms yet (the deterministic floor hasn't
//             arrived). enriching = the floor is showing but the LLM enrichment
//             is still in flight (④): the list renders, a "working" indicator
//             stays up, and the forward CTA is held until it settles. idle =
//             settled (enrichment reached a terminal alarm_analysis_ready, or the
//             watchdog released it). error = nothing arrived in time (retryable).
export type WatchApplyStatus =
  | { state: 'idle' }
  | { state: 'building' }
  | { state: 'enriching' }
  | { state: 'error'; message: string };

/** True while alarm generation/enrichment is still in flight (④): the forward
 *  CTA is held and a working indicator is shown. Pure — exported for tests. */
export function isAlarmWorking(status: WatchApplyStatus): boolean {
  return status.state === 'building' || status.state === 'enriching';
}

export interface RevealWatchProps {
  alarms: RevealAlarm[];
  /** The gateway-plan apply / alarm-build lifecycle, owned by the page. */
  status: WatchApplyStatus;
  /**
   * Per-recommendation accept/reject state (reveal-alarm-review A2/A3) —
   * derived from the mirrored snapshot upstream, never owned by this
   * component. Covers every `alarms[]` id (completeness by construction).
   */
  decisions: ReadonlyMap<string, boolean>;
  /** Flip one recommendation's decision. */
  onToggle: (recommendationId: string, accepted: boolean) => void;
  /**
   * The forward CTA. With alarms present this records the toggled decisions
   * then arms the accepted set (page-owned sequencing, contract §5.4); with
   * none, it's the unchanged navigational skip (no verb fired).
   */
  onStartWatching: () => void;
  /** True while the record→apply relay sequence is in flight — disables the CTA. */
  arming: boolean;
  /** True while the boat is unreachable — disables the CTA (arming needs the relay). */
  armingDisabled: boolean;
  /** Re-fire the gateway-plan apply after a build error. */
  onRetry: () => void;
  /**
   * The boat's network-health issues (FTUE↔cloud parity B2), mirrored
   * verbatim in the snapshot — used only to explain a MISSING alarm
   * recommendation ({@link alarmBlockerNote}). Read-only.
   */
  healthIssues: RevealHealthIssue[];
}

export function RevealWatch({
  alarms,
  status,
  decisions,
  onToggle,
  onStartWatching,
  arming,
  armingDisabled,
  onRetry,
  healthIssues,
}: RevealWatchProps): JSX.Element {
  const ordered = orderAlarmsForDisplay(alarms);
  const aiCount = ordered.filter((a) => a.aiSuggested === true).length;
  const summary = alarmsSummary(ordered.length, aiCount);
  const firstAiIndex = ordered.findIndex((a) => a.aiSuggested === true);
  const hasAlarms = ordered.length > 0;
  const blockers = alarmBlockerNote(healthIssues);
  const working = isAlarmWorking(status);
  // Gate the forward CTA only once alarms are actually being surfaced (④): a boat
  // with NO alarms must never be trapped behind a "Setting up…" button — the
  // deterministic floor may be empty (nothing to watch) or still incoming, and
  // either way the user can skip straight to the dashboard. Once the first alarm
  // shows (enriching), THEN hold the CTA so they don't advance mid-enrichment.
  const gated = working && hasAlarms;

  // Perceptible arrival (④): alarms (esp. late LLM suggestions) must enter the
  // list VISIBLY, not teleport in. Track the ids we've already shown; anything
  // new this render gets an `is-new` class that plays a one-shot slide/highlight.
  // The very first batch is seeded (not animated) so the whole floor doesn't
  // flash on arrival — only subsequent additions animate.
  const seenAlarmIdsRef = useRef<Set<string> | null>(null);
  const newAlarmIds = new Set<string>();
  if (seenAlarmIdsRef.current !== null) {
    for (const a of ordered) {
      if (!seenAlarmIdsRef.current.has(a.id)) newAlarmIds.add(a.id);
    }
  }
  useEffect(() => {
    seenAlarmIdsRef.current = new Set(ordered.map((a) => a.id));
  });

  // Presentation-only "Why?" expansion. Decisions stay exclusively on the
  // `decisions` prop / onToggle — never here.
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(() => new Set());
  const toggleWhy = (id: string): void =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Header: once alarms are present the quality-floor summary leads; before that,
  // an honest building / waiting line (never a permanent-sounding empty state).
  const title = hasAlarms
    ? summary.title
    : status.state === 'building'
      ? 'Setting up the alarms for your boat'
      : status.state === 'error'
        ? 'We hit a snag setting up your alarms'
        : 'Working out what to watch';
  const sub = hasAlarms
    ? summary.sub
    : status.state === 'building'
      ? 'Reading your equipment’s normal ranges and choosing what’s worth an alert — this only takes a moment.'
      : status.state === 'error'
        ? ''
        : 'We’ll line up the alarms that fit your boat as soon as your gateway is set up.';

  // One working line — shown inline beside the count (③) and mirrored in the
  // sticky footer. `building` = no alarms yet; `enriching` runs alongside the
  // settled floor. Carries the testids the old body lines used to.
  const statusText =
    status.state === 'building'
      ? 'Working out your alarms…'
      : 'Still checking for a few more suggestions…';
  const statusTestId =
    status.state === 'building' ? 'reveal-watch-building' : 'reveal-watch-enriching';

  return (
    <div className="reveal-stage reveal-watch" data-testid="reveal-stage-watch">
      {/* Header: count + inline working indicator, visible without scrolling (③).
          Reuses the shared teal-pulse motif so nothing new is introduced. */}
      <div className="reveal-watch__head">
        <h3 className="reveal-stage__title">{title}</h3>
        {working ? (
          <span className="reveal-watch__working" role="status" data-testid={statusTestId}>
            <span className="reveal-listening__pulse" aria-hidden="true" />
            <span>{statusText}</span>
          </span>
        ) : null}
      </div>
      {sub !== '' ? <p className="reveal-stage__sub">{sub}</p> : null}

      {blockers !== null ? (
        <div className="reveal-watch__blocked-note" data-testid="reveal-watch-blocked-note">
          <p className="reveal-watch__blocked-note__lead">
            Some alarms are waiting on a network issue:
          </p>
          <ul className="reveal-watch__blocked-note__list">
            {blockers.map((issue) => (
              <li key={issue.id}>
                <span className="reveal-watch__blocked-note__title">{issue.title}</span>
                {' — '}
                {issue.recommendedAction}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {hasAlarms ? (
        <ul className="reveal-alarms" data-testid="reveal-alarms">
          {ordered.map((a, i) => {
            const accepted = decisions.get(a.id) === true;
            const isOpen = openIds.has(a.id);
            const hasDetail = Boolean(a.explanation) || a.aiSuggested === true;
            const detailId = `reveal-alarm-why-panel-${a.id}`;
            return (
              <Fragment key={a.id}>
                {i === firstAiIndex && firstAiIndex > 0 ? (
                  <li className="reveal-alarms__divider" aria-hidden="true">
                    Suggestions to consider
                  </li>
                ) : null}
                <li
                  className={`reveal-alarm${a.aiSuggested === true ? ' is-suggested' : ''}${
                    newAlarmIds.has(a.id) ? ' is-new' : ''
                  }`}
                  data-testid={`reveal-alarm-${a.id}`}
                >
                  <div className="reveal-alarm__row">
                    <div className="reveal-alarm__ident">
                      <span className={`reveal-alarm__sev reveal-alarm__sev--${a.severity}`}>
                        {SEVERITY_LABELS[a.severity] ?? a.severity}
                      </span>
                      <span className="reveal-alarm__title">{a.title}</span>
                      {a.aiSuggested === true ? (
                        <span className="reveal-alarm__chip" title="Suggested by our setup helper">
                          Suggested
                        </span>
                      ) : null}
                      {a.confidence ? (
                        <span
                          className={`reveal-alarm__confidence reveal-alarm__confidence--${a.confidence}`}
                          title="How confident we are in this suggestion"
                          data-testid={`reveal-alarm-confidence-${a.id}`}
                        >
                          {a.confidence}
                        </span>
                      ) : null}
                    </div>
                    <div className="reveal-alarm__actions">
                      {hasDetail ? (
                        <button
                          type="button"
                          className="reveal-alarm__why-btn"
                          aria-expanded={isOpen}
                          aria-controls={detailId}
                          onClick={() => toggleWhy(a.id)}
                          data-testid={`reveal-alarm-why-${a.id}`}
                        >
                          {isOpen ? 'Hide' : 'Why?'}
                        </button>
                      ) : null}
                      <div
                        className="reveal-alarm__decision"
                        role="group"
                        aria-label={`Watch “${a.title}”?`}
                      >
                        <button
                          type="button"
                          className={`reveal-alarm__decision-btn${accepted ? ' is-active' : ''}`}
                          aria-pressed={accepted}
                          onClick={() => onToggle(a.id, true)}
                          data-testid={`reveal-alarm-accept-${a.id}`}
                        >
                          Watch
                        </button>
                        <button
                          type="button"
                          className={`reveal-alarm__decision-btn${!accepted ? ' is-active' : ''}`}
                          aria-pressed={!accepted}
                          onClick={() => onToggle(a.id, false)}
                          data-testid={`reveal-alarm-reject-${a.id}`}
                        >
                          Not now
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Collapsed-by-default explanation; expands in place (②). */}
                  {hasDetail ? (
                    <div
                      id={detailId}
                      className={`reveal-alarm__detail${isOpen ? ' is-open' : ''}`}
                    >
                      <div className="reveal-alarm__detail-inner">
                        {a.explanation ? (
                          <p className="reveal-alarm__why">{a.explanation}</p>
                        ) : null}
                        {a.aiSuggested === true ? (
                          <p className="reveal-alarm__hint">
                            A suggestion based on what we found — worth a look before you rely on
                            it.
                          </p>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </li>
              </Fragment>
            );
          })}
        </ul>
      ) : status.state === 'building' ? (
        // No alarms yet: honest progress radar (the inline header status carries
        // the announced text).
        <div className="reveal-radar is-thinking" aria-hidden="true">
          <span className="reveal-radar__ring" />
          <span className="reveal-radar__ring" />
          <span className="reveal-radar__dot" />
        </div>
      ) : null}

      {status.state === 'error' ? (
        <div className="reveal-block__note" role="alert" data-testid="reveal-watch-error">
          <p>{status.message}</p>
        </div>
      ) : null}

      {/* Sticky footer (③④): mirrored working state + gated CTA, always on-screen. */}
      <div className="reveal-watch__footer">
        {gated ? (
          <span className="reveal-watch__foot-status" aria-hidden="true">
            <span className="reveal-listening__pulse" />
            <span>Setting up…</span>
          </span>
        ) : null}
        <div className="reveal-watch__foot-actions">
          {status.state === 'error' ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={onRetry}
              data-testid="reveal-watch-retry"
            >
              Try again
            </button>
          ) : null}
          <button
            type="button"
            className={`btn ${status.state === 'error' ? 'btn-outline' : 'btn-primary'}`}
            onClick={onStartWatching}
            // Held while enrichment is in flight WITH alarms showing (gated), or
            // while arming / the boat is unreachable. A no-alarms boat is never
            // gated — it keeps the enabled "Continue to my boat" skip.
            disabled={gated || (hasAlarms && (arming || armingDisabled))}
            data-testid="reveal-watch-continue"
          >
            {gated
              ? 'Setting up…'
              : !hasAlarms
                ? 'Continue to my boat'
                : arming
                  ? 'Starting…'
                  : 'Start watching'}
          </button>
        </div>
        {/* FTUE→Alarms hand-off (UI-5): quietly promise this isn't a one-shot
            decision — the same alarms are editable later from the dashboard. Only
            shown when there ARE alarms to adjust. */}
        {hasAlarms ? (
          <p className="reveal-watch__footnote" data-testid="reveal-watch-footnote">
            You can adjust these anytime under Alarms in your boat&apos;s dashboard.
          </p>
        ) : null}
      </div>
    </div>
  );
}
